import classNames from 'classnames';
import { MouseEvent, useCallback, useEffect, useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import Collapsable from 'components/collapsable/Collapsable';
import TextAreaInput from 'components/forms/TextAreaInput';
import Icon from 'components/icons/Icon';
import { IconSize, IconSymbol } from 'components/icons/Icon.types';
import LibraryFile from 'components/library/LibraryFile';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import { useModal } from 'components/modal/ModalContext';
import DropMenu from 'components/navigation/DropMenu';
import {
  SortableItem,
  SortableItemType,
  SortableList,
  SortableListInfo,
} from 'components/sortables/Sortable';
import { useSortablePreview } from 'components/sortables/SortableContext';
import { IFolder } from 'features/folders/folderAPI';
import {
  deleteFolderAsync,
  patchFolderAsync,
  postFolderAsync,
} from 'features/folders/folderSlice';
import { AnyIdObject } from 'helpers/objects';
import { useInstanceId } from 'helpers/useInstance';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUser } from 'selectors/userSelectors';
import CreateFolderForm from './CreateFolderForm';
import { useLibraryFolderContext } from './FolderContext';

type LibraryFolderProps = {
  folder: IFolder;
  level?: number;
  listInfo: SortableListInfo;
  isRoot?: boolean;
  inRoot?: boolean;
};
function CollapsableLibraryFolder(props: LibraryFolderProps) {
  const { folder, level = 0 } = props;

  const { user } = useUser();
  const { openModal } = useModal();
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const confirmDelete = useConfirmDelete();
  const isPreview = useSortablePreview();
  const instanceId = useInstanceId('Folder' + folder.id);

  const [open, setOpen] = useState<boolean>(false);
  const { selectedFolderId, selectFolder, unselectFolder } =
    useLibraryFolderContext();
  const [editing, setEditing] = useState<boolean>(false);
  const [folderName, setFolderName] = useState<string>('');

  useEffect(() => {
    setFolderName(folder.folderName || '');
  }, [folder]);

  const toggleFolder = useCallback(() => {
    setOpen((prev) => {
      return !prev;
    });
  }, [folder]);

  const toggleSelectFolder = useCallback(() => {
    if (folder?.id) {
      if (!user) {
        setOpen((prev) => !prev);
        return;
      }
      if (selectedFolderId === folder.id) {
        unselectFolder();
      } else {
        selectFolder(folder.id);
        setOpen(true);
      }
      // showDebugInfo({ selectedFolderId, folderId: folder.id });
    }
  }, [folder, selectedFolderId]);

  function handleRenameFolder() {
    dispatch(
      patchFolderAsync({
        data: {
          ...folder,
          folderName: folderName,
        },
      }),
    ).then((response: any) => {
      switch (response.payload?.response.status) {
        case 200:
          break;
        default:
      }
      setEditing(false);
      setFolderName(folder.folderName || '');
    });
  }

  function handleFolderEdit() {
    folder.folderName && setFolderName(folder.folderName);
    setEditing(true);
  }

  function handleFolderDelete() {
    async function doDelete() {
      if (folder) {
        dispatch(deleteFolderAsync({ folder }));
      }
    }
    function confirmAgain() {
      confirmDelete({
        prompts: {
          title: <FormattedMessage id="GENERAL.DELETE.FOLDER.DELETE_CNF" />,
          prompt1: (
            <FormattedMessage
              id="GENERAL.DELETE.FOLDER.CNF2"
              values={{ foldername: `'${folder.folderName}'` }}
            />
          ),
          prompt2: <FormattedMessage id="GENERAL.DELETE.FOLDER.CNF3" />,
          serious: true,
          // TODO: password verification for confirmation
          // prompt2: <FormattedMessage id="PROJECTS.DELETE.CONFIRM_CNF2" />,
        },
      }).then((confirmed: boolean) => {
        if (confirmed) doDelete();
      });
    }
    confirmDelete({
      prompts: {
        prompt1: intl.formatMessage({ id: 'GENERAL.DELETE.FOLDER.CNF' }),
        prompt2: folder.folderName,
      },
    }).then((confirmed: boolean) => {
      if (confirmed) confirmAgain();
    });
  }

  function handleFolderNameChange(input: string, hasText: boolean) {
    setFolderName(input);
  }

  function handleBlur() {
    setEditing(false);
  }

  function createFolder(e: MouseEvent) {
    openModal(<CreateFolderForm onSubmit={submitFolder} />);
  }

  async function submitFolder(name: string): Promise<number> {
    return dispatch(
      postFolderAsync({
        data: {
          folderName: name,
          parentFolder: folder.id,
        },
      }),
    )
      .then((response: any) => {
        switch (response.payload?.response.status) {
          case 201:
            break;
          default:
            console.log(
              response,
              response?.payload.response.status,
              response?.payload.response.data?.message,
            );
        }
        return response.payload?.response.status;
      })
      .catch((error) => {
        return 500;
      });
  }

  return (
    <Collapsable
      className={classNames('folder', 'folder_level_' + level, {
        folder_selected: selectedFolderId === folder.id,
        subfolder: level > 1,
      })}
      dimension="height"
      style={{
        height: isPreview ? '35px' : undefined,
      }}
    >
      <Collapsable.Controller
        open={open && !isPreview}
        setOpen={toggleFolder}
        noPlus={true}
        icon={open ? IconSymbol.folderOpen : IconSymbol.folder}
        iconSize={24}
        iconIsTrigger={true}
        onClick={toggleSelectFolder}
      >
        <div className={classNames('files_description', { editing })}>
          {editing ? (
            <TextAreaInput
              // asInput={true}
              onChange={handleFolderNameChange}
              onBlur={handleBlur}
              onSubmit={handleRenameFolder}
              value={folderName}
            />
          ) : (
            <span>{folder.folderName?.trim()}</span>
          )}
        </div>
        {user ? (
          <div className="folder_menu">
            <DropMenu containerTailHeight={57}>
              <DropMenu.Trigger>
                <Icon
                  symbol={IconSymbol.ellipsis}
                  className="ellipsis"
                  // hoverVariant={IconVariant.dark}
                  size={IconSize.tiny}
                />
              </DropMenu.Trigger>
              <DropMenu.Items>
                <div className="menu_item" onClick={handleFolderEdit}>
                  <FormattedMessage id="LIBRARY.FOLDERS.EDIT" />
                  <Icon symbol={IconSymbol.pencil} size={22} />
                </div>
                <div className="menu_item" onClick={createFolder}>
                  <FormattedMessage id="LIBRARY.FOLDERS.CREATE" />
                  <Icon symbol={IconSymbol.folderAdd} size={24} />
                </div>
                <div className="menu_item" onClick={handleFolderDelete}>
                  <FormattedMessage id="X.DELETE" />
                  <Icon symbol={IconSymbol.bin} size={24} />
                </div>
              </DropMenu.Items>
            </DropMenu>
          </div>
        ) : null}
      </Collapsable.Controller>
      <Collapsable.Content open={open && !isPreview} dimension="height">
        <FolderContent {...props} />
      </Collapsable.Content>
    </Collapsable>
  );
}

export default function LibraryFolder(props: LibraryFolderProps) {
  const { listInfo, folder, inRoot } = props;
  const { user } = useUser();

  if (folder.folderName === undefined) return null;
  if (folder.folderName === '.') {
    return (
      <SortableList
        listInfo={listInfo}
        accepting={[SortableItemType.Folder, SortableItemType.File]}
        disabled={!user}
      >
        <FolderContent {...props} isRoot />
      </SortableList>
    );
  }

  return (
    <SortableItem
      listInfo={listInfo}
      item={folder as AnyIdObject}
      itemType={SortableItemType.Folder}
      accepting={[SortableItemType.Folder, SortableItemType.File]}
      noDropIndicator={true}
      className={classNames({
        inRoot: inRoot,
      })}
      disabled={!user}
    >
      <CollapsableLibraryFolder {...props} />
    </SortableItem>
  );
}

function FolderContent(props: LibraryFolderProps) {
  const { folder, level = 0, listInfo, isRoot } = props;

  const files = folder.files ?? [];
  const subFolders = folder.subFolders ?? [];

  return (
    <>
      {[...subFolders]
        .sort((a, b) => (a.folderName || '').localeCompare(b.folderName || ''))
        .map((fldr, i) => (
          <LibraryFolder
            key={`fldr.${level}.${i}`}
            folder={fldr}
            level={level + 1}
            listInfo={listInfo}
            inRoot={isRoot}
          />
        ))}
      {[...files]
        .sort((a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        )
        .map((f, i) => {
          return (
            <LibraryFile
              key={`file.${f.id}`}
              file={f}
              listInfo={listInfo}
              inRoot={isRoot}
            />
          );
        })}
    </>
  );
}
